import { useState } from "react";
import { Link } from "react-router-dom";
import type { IntegrityFinding } from "@/data/systemIntegrity";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertOctagon, AlertTriangle, Info, ExternalLink,
  CheckCircle2, XCircle, Wrench, User,
} from "lucide-react";
import { toast } from "sonner";

const SEVERITY_ICON: Record<string, React.ElementType> = {
  critical: AlertOctagon,
  warning: AlertTriangle,
  info: Info,
};

const SEVERITY_COLOR: Record<string, string> = {
  critical: "bg-destructive/10 text-destructive",
  warning: "bg-warning/10 text-warning",
  info: "bg-info/10 text-info",
};

interface Props {
  finding: IntegrityFinding;
  categoryLabel: string;
  entityLabel?: string;
  onDismiss: (id: string) => void;
  onApply: (id: string) => void;
}

export function SuggestedFixCard({ finding, categoryLabel, entityLabel, onDismiss, onApply }: Props) {
  const [status, setStatus] = useState<"pending" | "applied" | "dismissed">("pending");
  const Icon = SEVERITY_ICON[finding.severity] || Info;

  const handleApply = () => {
    setStatus("applied");
    onApply(finding.id);
    toast.success(`"${finding.title}" marked as resolved. Action logged.`);
  };

  const handleDismiss = () => {
    setStatus("dismissed");
    onDismiss(finding.id);
    toast(`"${finding.title}" dismissed. Will re-appear on next audit if unresolved.`);
  };

  if (status !== "pending") {
    return (
      <Card className="opacity-60 p-3">
        <div className="flex items-center gap-2 text-sm">
          {status === "applied" ? (
            <CheckCircle2 className="h-4 w-4 text-success" />
          ) : (
            <XCircle className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="line-through text-muted-foreground">{finding.title}</span>
          <Badge variant="outline" className="text-[10px] ml-auto">
            {status === "applied" ? "Resolved" : "Dismissed"}
          </Badge>
        </div>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="flex items-start gap-3 p-4">
        <div className={`mt-0.5 p-1.5 rounded-md ${SEVERITY_COLOR[finding.severity]}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{finding.title}</span>
            <Badge variant="outline" className="text-[10px]">{categoryLabel}</Badge>
             {finding.entityId && (
               <Badge variant="secondary" className="text-[10px]">{entityLabel || finding.entityId}</Badge>
             )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">{finding.detail}</p>
          <div className="flex items-center gap-1 mt-2 text-xs text-primary/80">
            <Wrench className="h-3 w-3" />
            <span className="italic">{finding.recommendation}</span>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <Button size="sm" variant="default" className="h-7 text-xs" onClick={handleApply}>
              <CheckCircle2 className="h-3 w-3 mr-1" /> Apply Fix
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleDismiss}>
              <XCircle className="h-3 w-3 mr-1" /> Dismiss
            </Button>
            {finding.actionLink && (
              <Link to={finding.actionLink}>
                <Button size="sm" variant="ghost" className="h-7 text-xs">
                  <ExternalLink className="h-3 w-3 mr-1" /> View
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
